import log from '@susu/log'
import type { AnalyticsEnvironment } from '@susu/segment-common'

/**
 * Where a surface is mounted. This is the one fact an application declares;
 * the Segment source it writes to and the consent posture it runs under both
 * follow from it (FC-183 §3/§5).
 *
 * - `website` — consumer-facing, on a page that carries the OneTrust banner.
 *   Includes tools used inside a store by a *customer* (check-in, check-out,
 *   the booking tool): the shopper's context is in-store, the deployment
 *   surface is the website.
 * - `inStore` — a colleague device (PredictSpring, RP webviews, iframe hosts).
 *   No consumer banner exists there, so there is no OptanonConsent cookie to
 *   gate on.
 */
export type Placement = 'website' | 'inStore'

/**
 * The Segment source each placement writes to, per environment — the single
 * definition for the whole monorepo. Applications pick a placement; they never
 * carry a write key.
 *
 * Previously every app hardcoded its own tst/acc/prd triple, which is how
 * Customer Hub and Active Customers List ended up on a third source set that
 * belonged to neither group.
 */
export const SEGMENT_SOURCES: Record<
  Placement,
  Record<AnalyticsEnvironment, string>
> = {
  website: {
    dev: 'Zy9h9uXcEt0hpJd9Xlz3AKbISpOdqBmN',
    tst: 'Zy9h9uXcEt0hpJd9Xlz3AKbISpOdqBmN',
    acc: 'vu6HG9YzL0vWMDsSiBz9jbF3TNXuU2US',
    prd: '4OhwIsrqFzQ0aeuYev8muGSWiimGtGIG',
    cn_tst: 'Zy9h9uXcEt0hpJd9Xlz3AKbISpOdqBmN',
    cn_prd: '4OhwIsrqFzQ0aeuYev8muGSWiimGtGIG',
  },
  inStore: {
    dev: 'TgLtItymcgnTvGzXl9bBGp5LuCqXD2fS',
    tst: 'TgLtItymcgnTvGzXl9bBGp5LuCqXD2fS',
    acc: '80tFeQG3E8EcPRmB6m1gjHkjaWW6NVRq',
    prd: 'uD76SgKOZpDHLj49u7kQnZgDHj1LUK05',
    cn_tst: 'TgLtItymcgnTvGzXl9bBGp5LuCqXD2fS',
    cn_prd: 'uD76SgKOZpDHLj49u7kQnZgDHj1LUK05',
  },
}

/** The Segment write key for a surface. Total over both unions. */
export const resolveSegmentWriteKey = (
  placement: Placement,
  environment: AnalyticsEnvironment,
): string => SEGMENT_SOURCES[placement][environment]

/**
 * The OneTrust domain-script id for suitsupply.com. One banner, one id — it
 * was hardcoded separately in check-in, check-out and booking-tool-v2.
 * `insertCookieTrackingScript` appends the `-test` suffix outside production.
 */
export const ONE_TRUST_DOMAIN_SCRIPT_ID = 'ea1e982c-e33d-42fb-ae31-a49f6d7b08cd'

const KNOWN_ENVIRONMENTS: readonly AnalyticsEnvironment[] = [
  'dev',
  'tst',
  'acc',
  'prd',
  'cn_tst',
  'cn_prd',
]

/**
 * Normalizes the many environment spellings in use (`PRD`, `Prd`, `prod`,
 * `production`, `cn-prd`) onto the canonical union, defaulting to `tst`.
 *
 * Case-insensitive on purpose: a host forwarding `'prd'`/`'Prd'` must never be
 * treated as non-production, which would pick the testing source and load the
 * `-test` consent script on production (review finding, PR 6820).
 *
 * The `tst` fallback is loud. It is how a missing or misspelt environment
 * variable ships as cross-environment traffic without anyone noticing — Size
 * Passport on acceptance ran on `MEAS_ENV=tst` and wrote acceptance events
 * into the testing Website source, which in turn pulled the testing GA4
 * container onto an acceptance page. The value still falls back, so nothing
 * breaks at runtime, but it says so.
 */
export const normalizeAnalyticsEnvironment = (
  value?: string,
): AnalyticsEnvironment => {
  const normalized = value?.trim().toLowerCase().replace(/-/g, '_')

  if (!normalized) {
    log.error(
      new Error(
        '[analytics-client] normalizeAnalyticsEnvironment received no environment — falling back to "tst". Events from this surface will go to the testing source.',
      ),
    )
    return 'tst'
  }
  if (normalized === 'prod' || normalized === 'production') {
    return 'prd'
  }
  if (normalized === 'test' || normalized === 'testing') {
    return 'tst'
  }
  if (normalized === 'acceptance') {
    return 'acc'
  }

  if (!KNOWN_ENVIRONMENTS.includes(normalized as AnalyticsEnvironment)) {
    log.error(
      new Error(
        `[analytics-client] normalizeAnalyticsEnvironment does not know "${value}" — falling back to "tst". Events from this surface will go to the testing source.`,
      ),
    )
    return 'tst'
  }

  return normalized as AnalyticsEnvironment
}

/** Production environments, for the Avo env and the OneTrust script suffix. */
export const isProductionEnvironment = (
  environment: AnalyticsEnvironment,
): boolean => environment === 'prd' || environment === 'cn_prd'
